import { gql } from '@apollo/client';

export const CREATE_USER = gql`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input) {
      id
      auth0Id
      email
      type
      firstName
      lastName
      birthDate
      timeZone
      profilePicUrl
      phoneNumber
      pushNotificationsEnabled
      isTestUser
    }
  }
`;

export const DELETE_USER_ACCOUNT = gql`
  mutation DeleteUserAccount {
    deleteUserAccount {
      id
      email
      firstName
      lastName
    }
  }
`;

export const UPDATE_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateNotificationPreferences(
    $input: UpdateNotificationPreferencesInput!
  ) {
    updateNotificationPreferences(input: $input) {
      id
      emailNotificationsEnabled
      pushNotificationsEnabled
    }
  }
`;

export const UPDATE_STYLIST = gql`
  mutation UpdateStylist($input: StylistInput!) {
    updateStylist(input: $input) {
      id
      bio
      landscapeImage
      portraitImages
      expertise
      aesthetics
      tiktok
      instagram
      substack
    }
  }
`;

export const UPDATE_STYLIST_STRIPE_SETUP = gql`
  mutation UpdateStylistStripeSetup($input: StylistStripeSetupInput!) {
    updateStylistStripeSetup(input: $input) {
      id
      stripeAccountId
    }
  }
`;

export const UPDATE_CLIENT = gql`
  mutation UpdateClient($input: ClientInput!) {
    updateClient(input: $input) {
      id
      aesthetics
      bio
      sizingDetails
      height
      shareAge
    }
  }
`;

export const TOGGLE_STYLIST_FAVORITE = gql`
  mutation ToggleStylistFavorite($input: ToggleStylistFavoriteInput!) {
    toggleStylistFavorite(input: $input)
  }
`;

export const START_VIDEO_SESSION = gql`
  mutation StartVideoSession($input: StartVideoSessionInput!) {
    startVideoSession(input: $input) {
      session {
        id
        sessionName
        hostId
        guestId
        consultationId
        isActive
        startedAt
        endedAt
      }
      zoomToken
      twilioToken
    }
  }
`;

export const END_VIDEO_SESSION = gql`
  mutation EndVideoSession($input: EndVideoSessionInput!) {
    endVideoSession(input: $input) {
      session {
        id
        sessionName
        hostId
        guestId
        isActive
        startedAt
        endedAt
        consultation {
          id
          clientId
          stylistId
          estimatedDuration
          stylistAttended
          clientAttended
          payment {
            id
            stripePaymentIntentId
          }
          stylist {
            id
            pricing {
              hourlyRate
            }
          }
        }
      }
      durationSeconds
    }
  }
`;

export const END_IN_PERSON_SESSION = gql`
  mutation EndInPersonSession($input: EndInPersonSessionInput!) {
    endInPersonSession(input: $input) {
      id
      status
      extraTimeMinutes
      estimatedDuration
      estimatedPrice
      startTime
      endTime
    }
  }
`;

export const VIDEO_SESSION_HEARTBEAT = gql`
  mutation VideoSessionHeartbeat($input: SendHeartbeatInput!) {
    videoSessionHeartbeat(input: $input)
  }
`;

export const CLIENT_EXIT_CALL = gql`
  mutation ClientExitCall($input: ClientExitCallInput!) {
    clientExitCall(input: $input)
  }
`;

export const CREATE_CLIENT_STRIPE_CUSTOMER = gql`
  mutation CreateClientStripeCustomer {
    createClientStripeCustomer {
      id
      aesthetics
      stripeCustomerId
      stripeDefaultPaymentMethodId
    }
  }
`;

export const SAVE_DEFAULT_PAYMENT_METHOD = gql`
  mutation SaveDefaultPaymentMethod($input: PaymentMethodInput!) {
    saveDefaultPaymentMethod(input: $input) {
      paymentMethodId
      last4
      brand
    }
  }
`;

export const CREATE_PAYMENT_INTENT = gql`
  mutation CreatePaymentIntent($input: CreatePaymentIntentInput!) {
    createPaymentIntent(input: $input) {
      id
      clientId
      stylistId
      authorizedAmount
      currency
      status
      captured
      stripePaymentIntentId
      stripePaymentMethodId
      stripeChargeId
    }
  }
`;

export const CREATE_CONSULTATION = gql`
  mutation CreateConsultation($input: CreateConsultationInput!) {
    createConsultation(input: $input) {
      id
      startTime
      stylist {
        id
        user {
          id
        }
      }
      client {
        id
        user {
          id
        }
      }
    }
  }
`;

export const MARK_NOTIFICATION_AS_READ = gql`
  mutation MarkNotificationAsRead($input: MarkNotificationAsReadInput!) {
    markNotificationAsRead(input: $input) {
      id
      userId
      type
      isRead
      deepLinkUrl
      targetType
      targetId
      variables
      openedAt
      createdAt
    }
  }
`;

export const CREATE_STYLIST_AVAILABILITY = gql`
  mutation CreateStylistAvailability($input: ManageAvailabilityInput!) {
    createStylistAvailability(input: $input) {
      id
      eventType
      availabilityCalendarIds
    }
  }
`;

export const UPDATE_STYLIST_AVAILABILITY = gql`
  mutation UpdateStylistAvailability($input: ManageAvailabilityInput!) {
    updateStylistAvailability(input: $input) {
      id
      config
      nylasConfigId
      eventType
    }
  }
`;

export const SET_DATE_AVAILABILITY = gql`
  mutation SetDateAvailability($input: SetDateAvailabilityInput!) {
    setDateAvailability(input: $input) {
      id
      date
      startTime
      endTime
    }
  }
`;

export const DISCONNECT_CALENDAR = gql`
  mutation DisconnectCalendar {
    disconnectCalendar {
      success
      message
    }
  }
`;

export const CANCEL_CONSULTATION = gql`
  mutation CancelConsultation($input: CancelConsultationInput!) {
    cancelConsultation(input: $input) {
      id
    }
  }
`;

export const UPDATE_CONSULTATION_PAYMENT_METHOD = gql`
  mutation UpdateConsultationPaymentMethod(
    $input: UpdateConsultationPaymentMethodInput!
  ) {
    updateConsultationPaymentMethod(input: $input) {
      id
      payment {
        id
        stripePaymentIntentId
        stripePaymentMethodId
        status
      }
    }
  }
`;

export const TOGGLE_INSTANT_ACTIVE = gql`
  mutation ToggleInstantActive($input: ToggleInstantActiveInput!) {
    toggleInstantActive(input: $input) {
      id
      stylistId
      isActive
      updatedAt
    }
  }
`;

export const CREATE_INSTANT_STYLING_REQUEST = gql`
  mutation CreateInstantStylingRequest(
    $input: CreateInstantStylingRequestInput!
  ) {
    createInstantStylingRequest(input: $input) {
      id
      clientId
      stylistId
      pricingTier
      duration
      reason
      gender
      age
      style
      details
    }
  }
`;

export const FIND_INSTANT_STYLING_MATCH = gql`
  mutation FindInstantStylingMatch($input: InstantStylingMatchInput!) {
    findInstantStylingMatch(input: $input) {
      resultCode
      stylist {
        id
        expertise
        aesthetics
        instagram
        tiktok
        bio
        landscapeImage
        portraitImages
        user {
          id
          firstName
          lastName
          gender
          birthDate
          profilePicUrl
        }
        pricing {
          id
          hourlyRate
          tierName
        }
      }
    }
  }
`;

export const CLIENT_ACCEPT_MATCH = gql`
  mutation ClientAcceptMatch($input: ClientAcceptMatchInput!) {
    clientAcceptMatch(input: $input) {
      clientUserId
      stylistUserId
      stylistAccepted
      clientAccepted
      paymentId
      consultationId
    }
  }
`;

export const STYLIST_ACCEPT_MATCH = gql`
  mutation StylistAcceptMatch($input: StylistAcceptMatchInput!) {
    stylistAcceptMatch(input: $input) {
      clientUserId
      stylistUserId
      stylistAccepted
      clientAccepted
      paymentId
      consultationId
    }
  }
`;

export const STYLIST_DECLINE_MATCH = gql`
  mutation StylistDeclineMatch($input: StylistDeclineMatchInput!) {
    stylistDeclineMatch(input: $input) {
      clientUserId
      stylistUserId
      stylistAccepted
      clientAccepted
      paymentId
      consultationId
      stylistDeclined
    }
  }
`;

export const CLIENT_CANCEL_REQUEST = gql`
  mutation ClientCancelRequest($input: ClientCancelRequestInput!) {
    clientCancelRequest(input: $input) {
      requestId
      clientUserId
      stylistUserId
      stylistAccepted
      clientAccepted
      paymentId
      consultationId
      stylistDeclined
      clientCancelled
    }
  }
`;

export const CLIENT_DECLINE_MATCH = gql`
  mutation ClientDeclineMatch($input: ClientDeclineMatchInput!) {
    clientDeclineMatch(input: $input) {
      requestId
      clientUserId
      stylistUserId
      stylistAccepted
      clientAccepted
      paymentId
      consultationId
      stylistDeclined
      clientDeclined
      clientCancelled
    }
  }
`;

export const STYLIST_ABSENT_TIMEOUT = gql`
  mutation StylistAbsentTimeout($input: StylistAbsentTimeoutInput!) {
    stylistAbsentTimeout(input: $input) {
      requestId
      clientUserId
      stylistUserId
      stylistAccepted
      clientAccepted
      paymentId
      consultationId
      stylistDeclined
      clientDeclined
      clientCancelled
    }
  }
`;

export const CREATE_SENDBIRD_SESSION_TOKEN = gql`
  mutation CreateSendbirdSessionToken {
    createSendbirdSessionToken {
      sessionToken
      expiresAt
    }
  }
`;

export const SAVE_PUSH_TOKEN = gql`
  mutation SavePushToken($input: SavePushTokenInput!) {
    savePushToken(input: $input) {
      id
      token
      platform
      deviceName
    }
  }
`;

export const UPDATE_USER_TIME_ZONE = gql`
  mutation UpdateUserTimeZone($input: UpdateUserTimeZoneInput!) {
    updateUserTimeZone(input: $input) {
      id
      timeZone
    }
  }
`;

export const CREATE_LOOKBOOK = gql`
  mutation CreateLookbook($input: CreateLookbookInput!) {
    createLookbook(input: $input) {
      id
      sendbirdMessageId
      stylistId
      clientId
      price
      note
      status
      paymentId
      stylist {
        id
        user {
          id
          firstName
          lastName
        }
      }
      client {
        id
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const UPDATE_LOOKBOOK_STATUS = gql`
  mutation UpdateLookbookStatus($input: UpdateLookbookStatusInput!) {
    updateLookbookStatus(input: $input) {
      id
      sendbirdMessageId
      stylistId
      clientId
      price
      note
      status
      paymentId
      stylist {
        id
        user {
          id
          firstName
          lastName
        }
      }
      client {
        id
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const GENERATE_SHARE_PROFILE_URL = gql`
  mutation GenerateShareProfileUrl {
    generateShareProfileUrl {
      shareProfileUrl
    }
  }
`;

export const CREATE_SIGNNOW_DOCUMENT = gql`
  mutation CreateSignNowDocument {
    createSignNowDocument {
      documentId
      signingLink
    }
  }
`;

export const CREATE_CLIENT_NOTE = gql`
  mutation CreateClientNote($input: CreateClientNoteInput!) {
    createClientNote(input: $input) {
      id
      clientId
      stylistId
      content
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_CLIENT_NOTE = gql`
  mutation DeleteClientNote($input: DeleteClientNoteInput!) {
    deleteClientNote(input: $input) {
      id
      clientId
      stylistId
      content
      createdAt
      updatedAt
    }
  }
`;

export const TRACK_USER_ACTIVITY = gql`
  mutation TrackUserActivity {
    trackUserActivity
  }
`;

export const UPDATE_USER_ADDRESS = gql`
  mutation UpdateUserAddress($input: UpdateAddressInput!) {
    updateUserAddress(input: $input) {
      id
      streetAddress
      city
      state
      zipCode
      country
      locationType
      accessDetails
    }
  }
`;

export const APPROVE_IN_PERSON_REQUEST = gql`
  mutation ApproveInPersonRequest($input: ApproveInPersonRequestInput!) {
    approveInPersonRequest(input: $input) {
      id
      status
      startTime
      endTime
      estimatedDuration
      estimatedPrice
      sessionType
      client {
        id
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const SUBMIT_SOURCED_BY_REQUEST = gql`
  mutation SubmitSourcedByRequest($input: SubmitSourcedByRequestInput!) {
    submitSourcedByRequest(input: $input)
  }
`;

export const DECLINE_IN_PERSON_REQUEST = gql`
  mutation DeclineInPersonRequest($input: DeclineInPersonRequestInput!) {
    declineInPersonRequest(input: $input) {
      id
      status
      client {
        id
        user {
          id
          firstName
          lastName
        }
      }
    }
  }
`;

export const SEND_BOOKING_REQUEST_NOTIFICATION = gql`
  mutation SendBookingRequestNotification(
    $input: SendBookingRequestNotificationInput!
  ) {
    sendBookingRequestNotification(input: $input)
  }
`;
